import React, { useState, useEffect } from "react";
import { X, Loader2, Save } from "lucide-react";
import { Hospital, CreateDoctorPayload, DoctorProfile } from "../../types";
import { updateDoctorData, getHospitals } from "../../services/doctorService";
import { toast } from "react-toastify";
import CustomSelect from "../Common/CustomSelect";

interface EditDoctorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  doctor: DoctorProfile | null;
}

const EditDoctorModal: React.FC<EditDoctorModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  doctor,
}) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    medicalRegistrationNumber: "",
    specialization: "",
    hospitalId: "",
    department: "",
    qualification: "",
    yearsOfExperience: "",
    licenseNumber: "",
    status: "Active" as "Active" | "Inactive",
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [isLoadingHospitals, setIsLoadingHospitals] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && doctor) {
      setFormData({
        firstName: doctor.firstName || "",
        lastName: doctor.lastName || "",
        email: doctor.email || "",
        phoneNumber: doctor.phoneNumber || "",
        medicalRegistrationNumber: doctor.medicalRegistrationNumber || "",
        specialization: doctor.specialization || "",
        hospitalId: doctor.hospitalId ? String(doctor.hospitalId) : "",
        department: doctor.department || "",
        qualification: doctor.qualification || "",
        yearsOfExperience:
          doctor.yearsOfExperience !== null && doctor.yearsOfExperience !== undefined
            ? String(doctor.yearsOfExperience)
            : "",
        licenseNumber: doctor.licenseNumber || doctor.medicalLicenseNumber || "",
        status: doctor.active ? "Active" : "Inactive",
      });
      setErrors({});
      setApiError(null);
      fetchHospitals();
    }
  }, [isOpen, doctor]);

  const fetchHospitals = async () => {
    setIsLoadingHospitals(true);
    try {
      const data = await getHospitals();
      setHospitals(data);
    } catch (err: any) {
      console.error("Failed to load hospitals:", err);
      toast.error("Failed to load hospitals dropdown list");
    } finally {
      setIsLoadingHospitals(false);
    }
  };

  if (!isOpen || !doctor) return null;

  const validate = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First Name is required";
    } else if (formData.firstName.trim().length > 100) {
      newErrors.firstName = "Max 100 characters allowed";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last Name is required";
    } else if (formData.lastName.trim().length > 100) {
      newErrors.lastName = "Max 100 characters allowed";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        newErrors.email = "Invalid email format";
      }
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone Number is required";
    } else {
      const phoneRegex = /^[\+]?[\d\s\-\(\)]{7,20}$/;
      if (!phoneRegex.test(formData.phoneNumber.trim())) {
        newErrors.phoneNumber = "Invalid phone number format (7-20 digits)";
      }
    }

    if (!formData.medicalRegistrationNumber.trim()) {
      newErrors.medicalRegistrationNumber = "Medical Registration Number is required";
    }

    if (!formData.specialization.trim()) {
      newErrors.specialization = "Specialization is required";
    }

    if (!formData.hospitalId) {
      newErrors.hospitalId = "Hospital selection is mandatory";
    }

    if (!formData.department.trim()) {
      newErrors.department = "Department is required";
    }

    if (!formData.qualification.trim()) {
      newErrors.qualification = "Qualification is required";
    }

    if (formData.yearsOfExperience === "" || formData.yearsOfExperience === null) {
      newErrors.yearsOfExperience = "Years of Experience is required";
    } else {
      const exp = Number(formData.yearsOfExperience);
      if (isNaN(exp) || exp < 0 || exp > 70) {
        newErrors.yearsOfExperience = "Enter valid years of experience (0-70)";
      }
    }

    if (!formData.licenseNumber.trim()) {
      newErrors.licenseNumber = "License Number is required";
    }

    if (!formData.status) {
      newErrors.status = "Status selection is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
    setApiError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError(null);

    if (!validate()) {
      toast.error("Please fix errors in the form before saving.");
      return;
    }

    setIsSubmitting(true);

    try {
      const payload: CreateDoctorPayload = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phoneNumber: formData.phoneNumber.trim(),
        medicalRegistrationNumber: formData.medicalRegistrationNumber.trim(),
        specialization: formData.specialization.trim(),
        hospitalId: Number(formData.hospitalId),
        department: formData.department.trim(),
        qualification: formData.qualification.trim(),
        yearsOfExperience: Number(formData.yearsOfExperience),
        licenseNumber: formData.licenseNumber.trim(),
        status: formData.status,
      };

      await updateDoctorData(doctor.userId, payload);
      toast.success("Doctor profile updated successfully!");
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error("Update doctor error:", err);
      const serverMessage =
        err.response?.data?.message || err.message || "Failed to update doctor";
      setApiError(serverMessage);
      toast.error(serverMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-3xl overflow-hidden my-8 max-h-[90vh] flex flex-col">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-primary-700 to-primary-800 text-white">
          <div>
            <h2 className="text-xl font-bold text-white">Edit Doctor Profile</h2>
            <p className="text-xs text-primary-100 mt-0.5">
              Update doctor information and details
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white hover:bg-white/10 p-1.5 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* API Error Banner */}
          {apiError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 font-medium">
              ⚠️ {apiError}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* 1. First Name */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="e.g. John"
                className={`input-field ${errors.firstName ? "border-red-500" : ""}`}
              />
              {errors.firstName && (
                <p className="mt-1 text-xs text-red-600">{errors.firstName}</p>
              )}
            </div>

            {/* 2. Last Name */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="e.g. Smith"
                className={`input-field ${errors.lastName ? "border-red-500" : ""}`}
              />
              {errors.lastName && (
                <p className="mt-1 text-xs text-red-600">{errors.lastName}</p>
              )}
            </div>

            {/* 3. Email */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="e.g. dr.smith@hospital.org"
                className={`input-field ${errors.email ? "border-red-500" : ""}`}
              />
              {errors.email && (
                <p className="mt-1 text-xs text-red-600">{errors.email}</p>
              )}
            </div>

            {/* 4. Phone Number */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                placeholder="e.g. +1-555-0192"
                className={`input-field ${errors.phoneNumber ? "border-red-500" : ""}`}
              />
              {errors.phoneNumber && (
                <p className="mt-1 text-xs text-red-600">{errors.phoneNumber}</p>
              )}
            </div>

            {/* 5. Medical Registration Number */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Medical Registration Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="medicalRegistrationNumber"
                value={formData.medicalRegistrationNumber}
                onChange={handleChange}
                placeholder="e.g. MRN-987654"
                className={`input-field ${
                  errors.medicalRegistrationNumber ? "border-red-500" : ""
                }`}
              />
              {errors.medicalRegistrationNumber && (
                <p className="mt-1 text-xs text-red-600">
                  {errors.medicalRegistrationNumber}
                </p>
              )}
            </div>

            {/* 6. License Number */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                License Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="licenseNumber"
                value={formData.licenseNumber}
                onChange={handleChange}
                placeholder="e.g. LIC-2024-88"
                className={`input-field ${errors.licenseNumber ? "border-red-500" : ""}`}
              />
              {errors.licenseNumber && (
                <p className="mt-1 text-xs text-red-600">{errors.licenseNumber}</p>
              )}
            </div>

            {/* 7. Hospital Selection */}
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Hospital <span className="text-red-500">*</span>
              </label>
              {isLoadingHospitals ? (
                <div className="flex items-center space-x-2 text-sm text-gray-500 py-2">
                  <Loader2 className="h-4 w-4 animate-spin text-primary-600" />
                  <span>Loading available hospitals...</span>
                </div>
              ) : (
                <CustomSelect
                  value={formData.hospitalId}
                  onChange={(val) => {
                    handleChange({
                      target: { name: "hospitalId", value: val },
                    } as React.ChangeEvent<HTMLSelectElement>);
                  }}
                  options={[
                    { value: "", label: "-- Select Hospital --" },
                    ...hospitals.map((h) => ({
                      value: String(h.id),
                      label: `${h.hospitalName} (${h.hospitalCode})`,
                    })),
                  ]}
                  error={!!errors.hospitalId}
                />
              )}
              {errors.hospitalId && (
                <p className="mt-1 text-xs text-red-600">{errors.hospitalId}</p>
              )}
            </div>

            {/* 8. Specialization */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Specialization <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="specialization"
                value={formData.specialization}
                onChange={handleChange}
                placeholder="e.g. Oncology / Hematology"
                className={`input-field ${
                  errors.specialization ? "border-red-500" : ""
                }`}
              />
              {errors.specialization && (
                <p className="mt-1 text-xs text-red-600">{errors.specialization}</p>
              )}
            </div>

            {/* 9. Department */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Department <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="department"
                value={formData.department}
                onChange={handleChange}
                placeholder="e.g. Clinical Hematology"
                className={`input-field ${errors.department ? "border-red-500" : ""}`}
              />
              {errors.department && (
                <p className="mt-1 text-xs text-red-600">{errors.department}</p>
              )}
            </div>

            {/* 10. Qualification */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Qualification <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="qualification"
                value={formData.qualification}
                onChange={handleChange}
                placeholder="e.g. MBBS, MD, FRCP"
                className={`input-field ${
                  errors.qualification ? "border-red-500" : ""
                }`}
              />
              {errors.qualification && (
                <p className="mt-1 text-xs text-red-600">{errors.qualification}</p>
              )}
            </div>

            {/* 11. Years of Experience */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Years of Experience <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="yearsOfExperience"
                min="0"
                max="70"
                value={formData.yearsOfExperience}
                onChange={handleChange}
                placeholder="e.g. 8"
                className={`input-field ${
                  errors.yearsOfExperience ? "border-red-500" : ""
                }`}
              />
              {errors.yearsOfExperience && (
                <p className="mt-1 text-xs text-red-600 font-medium">
                  {errors.yearsOfExperience}
                </p>
              )}
            </div>

            {/* 12. Status */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Status <span className="text-red-500">*</span>
              </label>
              <CustomSelect
                value={formData.status}
                onChange={(val) => {
                  setFormData((prev) => ({ ...prev, status: val as "Active" | "Inactive" }));
                  if (errors.status) setErrors((prev) => ({ ...prev, status: "" }));
                }}
                options={[
                  { value: "Active", label: "Active" },
                  { value: "Inactive", label: "Inactive" },
                ]}
                error={!!errors.status}
              />
              {errors.status && (
                <p className="mt-1 text-xs text-red-600">{errors.status}</p>
              )}
            </div>
          </div>

          {/* Form Actions Footer */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="btn-secondary text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary text-sm flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Updating...</span>
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  <span>Update Doctor</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditDoctorModal;
